import { createFileRoute, Link } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { api } from "@convex/_generated/api"
import type { Id } from "@convex/_generated/dataModel"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { ChevronRight, Users, UserPlus } from "lucide-react"
import { CountryCell } from "./-components"

export const Route = createFileRoute("/(app)/app/results/teams/$teamId")({
  component: TeamProfilePage,
})

function TeamProfilePage() {
  const { teamId } = Route.useParams()

  const profileData = useQuery(api.quizzes.handlers.getTeamProfileStats, {
    teamId: teamId as Id<"teams">,
  })

  if (profileData === undefined) {
    return (
      <div className="py-8 max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  if (!profileData) {
    return (
      <div className="py-8 max-w-5xl mx-auto">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Team Not Found</h3>
            <p className="text-sm text-muted-foreground">
              This team profile does not exist.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  const { team, stats, gameHistory, members } = profileData
  const isTeamsCompetition = team.type === "teams"
  const Icon = isTeamsCompetition ? Users : UserPlus

  // Get initials for avatar
  const initials = team.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)

  return (
    <div className="py-8 max-w-5xl mx-auto space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center text-sm text-muted-foreground">
        <Link to="/app/results" className="hover:text-foreground transition-colors">
          Results
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <Link
          to={isTeamsCompetition ? "/app/results/teams" : "/app/results/pairs"}
          className="hover:text-foreground transition-colors"
        >
          {isTeamsCompetition ? "Teams" : "Pairs"}
        </Link>
        <ChevronRight className="h-4 w-4 mx-2" />
        <span className="text-foreground font-medium">{team.name}</span>
      </nav>

      {/* Header / Team Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-2xl font-bold shrink-0">
              {initials}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold">{team.name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2 text-muted-foreground">
                <Icon className="h-4 w-4" />
                <span className="capitalize">{team.type}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Games Played</div>
            <div className="text-2xl font-bold">{stats.gamesPlayed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">
              {isTeamsCompetition ? "Total Points" : "Total Score"}
            </div>
            <div className="text-2xl font-bold">
              {isTeamsCompetition
                ? stats.totalPoints?.toLocaleString() ?? 0
                : stats.totalRawScore}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">
              {isTeamsCompetition ? "Best 9" : "Average Score"}
            </div>
            <div className="text-2xl font-bold text-emerald-600">
              {isTeamsCompetition
                ? stats.best9?.toLocaleString() ?? 0
                : stats.gamesPlayed > 0
                  ? Math.round(stats.totalRawScore / stats.gamesPlayed)
                  : 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-sm text-muted-foreground mb-1">Best Week Score</div>
            <div className="text-2xl font-bold">{stats.bestWeekScore}</div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Game History */}
        <Card>
          <CardHeader>
            <CardTitle>Game History</CardTitle>
          </CardHeader>
          <CardContent>
            {gameHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No games played yet.
              </p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {gameHistory.map((game) => (
                  <div
                    key={game.quizId}
                    className="flex items-center justify-between py-2 border-b border-border/50 last:border-0"
                  >
                    <span className="text-sm font-medium">Week {game.weekNumber}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground tabular-nums">
                        {game.rawScore} pts
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          game.rank === 1
                            ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        #{game.rank}
                        {isTeamsCompetition && game.points !== null && (
                          <span className="ml-1">→ {game.points} pts</span>
                        )}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card>
          <CardHeader>
            <CardTitle>Team Members</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                No members found.
              </p>
            ) : (
              members.map((member) => {
                // Get initials for member avatar
                const memberInitials = member.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2)

                // Generate a consistent color based on the name
                const colors = [
                  "from-indigo-500 to-purple-600",
                  "from-pink-500 to-rose-600",
                  "from-cyan-500 to-blue-600",
                  "from-amber-500 to-orange-600",
                  "from-emerald-500 to-green-600",
                  "from-violet-500 to-purple-600",
                ]
                const colorIndex =
                  member.name.charCodeAt(0) % colors.length
                const gradient = colors[colorIndex]

                return (
                  <Link
                    key={member.userId}
                    to="/app/results/players/$userId"
                    params={{ userId: member.userId }}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border hover:border-border transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-white text-sm font-bold`}
                      >
                        {memberInitials}
                      </div>
                      <div>
                        <span className="font-medium">{member.name}</span>
                        {member.country && (
                          <div className="text-xs text-muted-foreground">
                            <CountryCell country={member.country} showName={false} />
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </Link>
                )
              })
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
